import React, { useEffect, useCallback, useState, useRef } from "react";
import { array, bool, object } from "prop-types";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { StoryElementCard } from "../../../arrow/components/Molecules/StoryElementCard";
import { StoryTags } from "../../../arrow/components/Atoms/StoryTags";
import { Paywall } from "../../../arrow/components/Rows/Paywall";
import AdComponent from "../../molecules/AdComponent";
import MoreAboutAuthors from "../../../arrow/components/Atoms/MoreAboutAuthors";
import { useDeviceType } from "../../customHooks/useDeviceType";
import "./story-elements.m.css";

const TABOOLA_SCROLL_THRESHOLD_DESKTOP = 300;
const TABOOLA_SCROLL_THRESHOLD_MOBILE = 400;

const RenderCards = ({ story, cards, recommendedStories }) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const isMagazineStory = story["story-template"] === "magazine";
  const isStoryBehindPaywall = !member && isMagazineStory;
  const isDesktop = useDeviceType() === "desktop";
  const storyHeadline = get(story, ["headline"], "");
  const storyMetadata = get(story, ["metadata"], "");

  const { creatorYoutubeCards } = cards.reduce(
    (acc, card) => {
      const cardTypes = get(card, ["metadata", "attributes", "cardtype"], []);

      if (Array.isArray(cardTypes) && cardTypes.includes("Creator Youtube")) {
        acc.creatorYoutubeCards.push(card);
      }

      return acc;
    },
    { creatorYoutubeCards: [] }
  );

  const creatorYoutubeVideos = creatorYoutubeCards.flatMap((card) =>
    get(card, ["story-elements"], []).filter((el) => el.type === "youtube-video" && el["embed-url"])
  );

  const getYoutubeVideoId = (url = "") => {
    try {
      if (url.includes("embed")) {
        return url.split("/embed/")[1].split("?")[0];
      }
      if (url.includes("watch")) {
        return new URL(url).searchParams.get("v");
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const getYoutubeThumbnail = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  const socialLinks = {
    facebook: get(storyMetadata, ["creator-facebook-handle"], "").trim(),
    twitter: get(storyMetadata, ["creator-twitter-handle"], "").trim(),
    youtube: get(storyMetadata, ["creator-youtube-handle"], "").trim(),
    instagram: get(storyMetadata, ["creator-instagram-handle"], "").trim(),
  };

  const hasAllSocialLinks = Object.values(socialLinks).some(Boolean);

  const Ad = useCallback(
    (props) => <AdComponent {...props} recommendedStories={recommendedStories} isDesktop={isDesktop} story={story} />,
    [recommendedStories, isDesktop, story?.id]
  );

  return (
    <>
      {isStoryBehindPaywall ? (
        <>
          <StoryElementCard
            story={story}
            card={cards[0]}
            key={get(cards[0], ["id"], "")}
            recommendedStories={recommendedStories}
            adComponent={Ad}
          />
          <Paywall magazine={true} />
        </>
      ) : (
        cards.map((card, index) => (
          <div key={index}>
            <StoryElementCard card={card} story={story} recommendedStories={recommendedStories} adComponent={Ad} />
          </div>
        ))
      )}
      {hasAllSocialLinks && (
        <div styleName="social-container">
          <div styleName="inline-video-heading">{storyHeadline}&apos;s Social Presence</div>

          <ul>
            {socialLinks.facebook && (
              <li>
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                  <i styleName="fbicon" /> Facebook
                </a>
              </li>
            )}

            {socialLinks.twitter && (
              <li>
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                  <i styleName="xicon" /> X
                </a>
              </li>
            )}

            {socialLinks.youtube && (
              <li>
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                  <i styleName="youtubeicon" /> YouTube
                </a>
              </li>
            )}

            {socialLinks.instagram && (
              <li>
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <i styleName="instaicon" /> Instagram
                </a>
              </li>
            )}
          </ul>
        </div>
      )}

      {!isStoryBehindPaywall && creatorYoutubeVideos.length > 0 && (
        <div styleName="creator-video-container">
          <div styleName="creator-video-containerIn">
            <div styleName="inline-video-heading">Popular Content from {storyHeadline}</div>
            <div styleName="creator-video-outer">
              {creatorYoutubeVideos.map((video, index) => {
                const videoId = getYoutubeVideoId(video["embed-url"] || video.url);

                if (!videoId) return null;

                return (
                  <div styleName="creator-video-box" key={video.id || index}>
                    <a href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noopener noreferrer">
                      <div styleName="inline-frame-box">
                        <i styleName="youtube-icon" />
                        <img src={getYoutubeThumbnail(videoId)} alt="YouTube video thumbnail" loading="lazy" />
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

RenderCards.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
};

const RenderStoryFooter = ({ story, taboola }) => {
  const qtState = useSelector((state) => state);
  const member = useSelector((state) => get(state, ["member"], null));
  const isMagazineStory = story["story-template"] === "magazine";
  const isStoryBehindPaywall = !member && isMagazineStory;
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const isWebView = get(qtState, ["qt", "config", "isWebView"]) === true;

  if (!isAdfreePage && isStoryBehindPaywall) {
    return null;
  }

  return (
    <>
      {!isWebView && <StoryTags tags={story.tags} template="noBorder" story={story} />}

      <MoreAboutAuthors authors={story.authors} story={story} />

      {taboola && (
        <>
          <div id="taboola-below-article-thumbnails" className="TABOOLA"></div>
          <div
            dangerouslySetInnerHTML={{
              __html: `
                  <script type="text/javascript">
                    window._taboola = window._taboola || [];
                    _taboola.push({
                      mode: 'alternating-thumbnails-a',
                      container: 'taboola-below-article-thumbnails',
                      placement: 'Below Article Thumbnails',
                      target_type: 'mix'
                    });
                  </script>
              `,
            }}
          />
        </>
      )}
    </>
  );
};

RenderStoryFooter.propTypes = {
  story: object,
  taboola: bool,
};

const StoryElements = ({ story, cards, recommendedStories, config }) => {
  const qtState = useSelector((state) => state);
  const [ClientEnhancers, setClientEnhancers] = useState(null);
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const taboola = get(config, ["publisher-attributes", "taboola"], true);
  const isWebView = get(qtState, ["qt", "config", "isWebView"]) === true;
  const isCreatorStory = get(story, ["story-template"], "") === "creator";
  const [hasReachedTaboolaScrollThreshold, setHasReachedTaboolaScrollThreshold] = useState(false);
  const initialScrollYRef = useRef(0);
  const contentRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    import("./client-enhancers").then((module) => {
      if (isMounted) {
        setClientEnhancers(() => module.default);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    initialScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const isMobile = window.innerWidth < 768;
      const scrollThreshold = isMobile ? TABOOLA_SCROLL_THRESHOLD_MOBILE : TABOOLA_SCROLL_THRESHOLD_DESKTOP;
      const scrolledDistance = Math.abs(window.scrollY - initialScrollYRef.current);

      if (scrolledDistance >= scrollThreshold) {
        setHasReachedTaboolaScrollThreshold(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (hasReachedTaboolaScrollThreshold && taboola === true && !isCreatorStory && !isWebView && !isAdfreePage) {
      window._taboola = window._taboola || [];
      window._taboola.push({ article: "auto" });

      if (!document.getElementById("tb_loader_script")) {
        const s = document.createElement("script");
        s.async = true;
        s.src = "//cdn.taboola.com/libtrc/jambojar-fortuneindia/loader.js";
        s.id = "tb_loader_script";

        const f = document.getElementsByTagName("script")[0];
        f.parentNode.insertBefore(s, f);
      }

      if (window.performance && typeof window.performance.mark === "function") {
        window.performance.mark("tbl_ic");
      }
    }
  }, [hasReachedTaboolaScrollThreshold, isAdfreePage, isCreatorStory, isWebView, taboola]);

  return (
    <div
      id="story-wrapper"
      ref={contentRef}
      className={isCreatorStory ? "container" : ""}
      styleName="story-elements-wrapper"
    >
      <div styleName={isCreatorStory ? "creator-content" : "cards-wrapper"}>
        <RenderCards story={story} cards={cards} recommendedStories={recommendedStories} />
        {ClientEnhancers && <ClientEnhancers rootRef={contentRef} story={story} />}
        <RenderStoryFooter story={story} taboola={taboola} />
      </div>
    </div>
  );
};

StoryElements.propTypes = {
  config: object,
  story: object,
  cards: array,
  recommendedStories: array,
};

export default StoryElements;
